import { AsyncLocalStorage } from 'node:async_hooks';
import { StreamedRunResult } from '../result';
import { getGlobalTraceProvider } from './provider';
import type { Span, SpanError } from './spans';
import type { Trace, TraceOptions } from './traces';

type ContextState = {
  trace?: Trace;
  span?: Span<any>;
  previousSpan?: Span<any>;
  active: boolean;
};

const ALS_SYMBOL = Symbol.for('openai.agents.core.asyncLocalStorage');
let localFallbackAls: AsyncLocalStorage<ContextState | undefined> | undefined;

// 全局 Symbol 确保即使加载了多份 agents-core 副本
//（例如通过不同的 npm 解析路径或打包工具），它们都共享同一个
// AsyncLocalStorage 实例。这能防止当下游包引入重复副本时丢失追踪/跨度状态。
function getContextAsyncLocalStorage() {
  try {
    const globalScope = globalThis as unknown as Record<
      symbol | string,
      AsyncLocalStorage<ContextState | undefined> | undefined
    >;

    const globalALS = globalScope[ALS_SYMBOL];

    if (globalALS) {
      return globalALS;
    }

    const newALS = new AsyncLocalStorage<ContextState | undefined>();
    globalScope[ALS_SYMBOL] = newALS;
    return newALS;
  } catch {
    // 作为防御性的回退方案（例如当 globalThis 被锁定，或在受限运行时环境中
    // ALS 构造抛出异常时），保留一个模块级别的本地 ALS，以便追踪功能仍能正常运行，而不是让调用方崩溃。
    if (!localFallbackAls) {
      localFallbackAls = new AsyncLocalStorage<ContextState | undefined>();
    }
    return localFallbackAls;
  }
}

/**
 * 此函数会检查执行上下文中是否已有活跃的追踪。如果有，它将在现有追踪下运行给定的函数。
 * 如果没有追踪，它将创建一个新的追踪，并将其分配给该函数的执行上下文。
 *
 * @param fn - 要运行并分配追踪上下文的函数。
 * @param options - 用于创建追踪的选项。
 */
export async function getOrCreateTrace<T>(
  fn: () => Promise<T>,
  options: TraceOptions = {},
): Promise<T> {
  const currentTrace = getCurrentTrace();
  if (currentTrace) {
    return await fn();
  }

  const newTrace = getGlobalTraceProvider().createTrace(options);

  const newContext: ContextState = {
    trace: newTrace,
    active: true,
  };
  const previousAlsStore = getContextAsyncLocalStorage().getStore();
  return getContextAsyncLocalStorage().run(
    newContext,
    _wrapFunctionWithTraceLifecycle(fn, newContext, previousAlsStore),
  );
}

/**
 * This function will get the current trace from the execution context.
 *
 * @returns The current trace or null if there is no trace.
 */
export function getCurrentTrace() {
  const currentTrace = getActiveContext();
  if (currentTrace?.trace) {
    return currentTrace.trace;
  }

  return null;
}

function getActiveContext() {
  const store = getContextAsyncLocalStorage().getStore();
  if (store?.active === true) {
    return store;
  }
  return undefined;
}

export function getCurrentSpan() {
  const currentSpan = getActiveContext();
  if (currentSpan?.span) {
    return currentSpan.span;
  }
  return null;
}

/**
 * This is an AsyncLocalStorage instance that stores the current trace.
 * It will automatically handle the execution context of different event loop executions.
 *
 * The functions below should be the only way that this context gets interfaced with.
 */
function _wrapFunctionWithTraceLifecycle<T>(
  fn: (trace: Trace) => Promise<T>,
  currentContext: ContextState,
  previousAlsStore?: ContextState,
) {
  return async () => {
    const trace = getCurrentTrace();
    if (!trace) {
      throw new Error('No trace found');
    }

    let cleanupDeferred = false;
    let started = false;

    const cleanupContext = () => {
      currentContext.active = false;
      currentContext.trace = undefined;
      currentContext.span = undefined;
      currentContext.previousSpan = undefined;
      getContextAsyncLocalStorage().enterWith(previousAlsStore);
    };

    try {
      await trace.start();
      started = true;

      const result = await fn(trace);

      // If result is a StreamedRunResult, defer trace end until stream loop completes
      if (result instanceof StreamedRunResult) {
        const streamLoopPromise = result._getStreamLoopPromise();
        if (streamLoopPromise) {
          cleanupDeferred = true;
          streamLoopPromise.finally(async () => {
            try {
              if (started) {
                await trace.end();
              }
            } finally {
              cleanupContext();
            }
          });

          return result;
        }
      }

      // For non-streaming results, end trace synchronously
      if (started) {
        await trace.end();
      }

      return result;
    } finally {
      // If cleanup was deferred to the streaming loop, keep the context marked
      // active so concurrent traces do not clear it prematurely. Otherwise,
      // mark inactive and restore now.
      if (!cleanupDeferred) {
        cleanupContext();
      }
    }
  };
}

/**
 * This function will clone the current context by creating new instances of the trace, span, and
 * previous span.
 *
 * @param context - The context to clone.
 * @returns A clone of the context.
 */
export function cloneCurrentContext(context: ContextState) {
  return {
    trace: context.trace?.clone(),
    span: context.span?.clone(),
    previousSpan: context.previousSpan?.clone(),
    active: context.active,
  };
}

/**
 * This function will run the given function with a new span context.
 *
 * @param fn - The function to run with the new span context.
 */
export function withNewSpanContext<T>(fn: () => Promise<T>) {
  const currentContext = getActiveContext();
  if (!currentContext) {
    return fn();
  }

  const copyOfContext = cloneCurrentContext(currentContext);
  return getContextAsyncLocalStorage().run(copyOfContext, fn);
}

export function setCurrentSpan(span: Span<any>) {
  const context = getActiveContext();
  if (!context) {
    throw new Error('No existing trace found');
  }

  if (context.span) {
    context.span.previousSpan = context.previousSpan;
    context.previousSpan = context.span;
  }

  span.previousSpan = context.span ?? context.previousSpan;
  context.span = span;
  getContextAsyncLocalStorage().enterWith(context);
}

export function resetCurrentSpan() {
  const context = getActiveContext();
  if (context) {
    context.span = context.previousSpan;
    context.previousSpan = context.previousSpan?.previousSpan;
    getContextAsyncLocalStorage().enterWith(context);
  }
}

/**
 * This function will create a new trace and assign it to the execution context of the function
 * passed to it.
 *
 * @param fn - The function to run and assign the trace context to.
 * @param options - Options for the creation of the trace
 */

export async function withTrace<T>(
  trace: string | Trace,
  fn: (trace: Trace) => Promise<T>,
  options: TraceOptions = {},
): Promise<T> {
  const newTrace =
    typeof trace === 'string'
      ? getGlobalTraceProvider().createTrace({
          ...options,
          name: trace,
        })
      : trace;

  const context: ContextState = {
    trace: newTrace,
    active: true,
  };
  const previousAlsStore = getContextAsyncLocalStorage().getStore();

  return getContextAsyncLocalStorage().run(
    context,
    _wrapFunctionWithTraceLifecycle(fn, context, previousAlsStore),
  );
}

/**
 * This function will add an error to the current span.
 *
 * @param spanError - The error to add to the current span.
 */
export function addErrorToCurrentSpan(spanError: SpanError) {
  const currentSpan = getCurrentSpan();
  if (currentSpan) {
    currentSpan.setError(spanError);
  }
}
